import { useClinicalRecordLock } from '../../../../../components/clinical-record-lock';
import { useForm } from 'react-hook-form';
import { IAnteriorEye } from '../../../../../@types/anteriorEye.type';
import FormProvider from '../../../../../components/hook-form/FormProvider';
import Keratometry from './Keratometry';
import LeftAndRightAnteriorEye from './LeftAndRightAnteriorEye';
import { Box, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { AnteriorEyeService } from '../../../../../api/contact-lenses/anterior-eye.service';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../../../utils/util';
import { useEffect, useState } from 'react';
import { IClinicalRecordSchema } from '../../../../../@types/clinicalRecord.type';
import { useAppContext } from '../../../../../providers/AppContext';

type Props = {
  anteriorEye: IAnteriorEye;
  clinicalRecordId: number;
  tab: (type: string) => void;
  showCLForm: boolean;
  getAnteriorEyeRecords: (anteriorEyeId: number, clinicalId: number) => void;
  onUpdate?: () => Promise<any> | void;
  selectedRow: number;
  clinicalRecords: IClinicalRecordSchema[];
};

const AnteriorEye = ({
  anteriorEye,
  clinicalRecordId,
  tab,
  showCLForm,
  getAnteriorEyeRecords,
  onUpdate,
  selectedRow,
  clinicalRecords,
}: Props) => {
  const lock = useClinicalRecordLock();
  const [leftHVID, setLeftHVID] = useState('');
  const [rightHVID, setRightHVID] = useState('');
  const methods = useForm<IAnteriorEye>();
  const { handleSubmit, reset } = methods;
  const {
    formState: { isSubmitting },
  } = methods;

  const { patientId } = useParams();
  const { getAllClinicalRecords } = useAppContext();

  const onSubmit = async (data: IAnteriorEye) => {
    const anteriorEyeService = new AnteriorEyeService();
    try {
      const updatedData: IAnteriorEye = {
        ...data,
        leftAndRightAnteriorEye: {
          ...data.leftAndRightAnteriorEye,
          leftHVID,
          rightHVID,
        },
      };
      // Amending a signed record: stage the change instead of saving it.
      if (
        lock.stageIfAmending('anteriorEye', {
          ...updatedData,
          clinicalRecordId,
        } as Record<string, unknown>)
      ) {
        tab('cl_fitting');
        return;
      }
      const response = await anteriorEyeService.createAnteriorEye(patientId!, {
        ...updatedData,
        clinicalRecordId,
      });
      enqueueSnackbar('Anterior Eye Created Successfully!', {
        variant: 'success',
      });
      if (onUpdate) {
        await onUpdate();
      } else {
        getAllClinicalRecords(patientId);
      }
      getAnteriorEyeRecords(response.data?.id, clinicalRecordId);
      tab('cl_fitting');
    } catch (error: unknown) {
      // `error.message[0]` showed the first *character* of a string message.
      enqueueSnackbar(
        getApiErrorMessage(error, 'Failed to save anterior eye'),
        {
          variant: 'error',
        },
      );
    }
  };

  useEffect(() => {
    if (showCLForm || !anteriorEye || Object.keys(anteriorEye).length === 0) {
      reset({
        keratometry: {
          rightHoz1: '',
          rightHoz2: '',
          rightHeight1: '',
          rightHeight2: '',
          leftHoz1: '',
          leftHoz2: '',
          leftHeight1: '',
          leftHeight2: '',
        },
        leftAndRightAnteriorEye: {
          leftPupilSize: '',
          rightPupilSize: '',
          leftHVID: '',
          rightHVID: '',
          leftConjunctivalRedness: '',
          rightConjunctivalRedness: '',
          leftCornealInfiltrates: '',
          rightCornealInfiltrates: '',
          leftCornealNeovascularisation: '',
          rightCornealNeovascularisation: '',
          leftCornealOedema: '',
          rightCornealOedema: '',
          leftCornealStaining: '',
          rightCornealStaining: '',
          leftEndothelialBlebs: '',
          rightEndothelialBlebs: '',
          leftEndothelialPolymegethism: '',
          rightEndothelialPolymegethism: '',
          leftEpithelialMicrocysts: '',
          rightEpithelialMicrocysts: '',
          leftLimbalRedness: '',
          rightLimbalRedness: '',
          leftMeibomianDysfunction: '',
          rightMeibomianDysfunction: '',
          leftPapillaryConjunctivitis: '',
          rightPapillaryConjunctivitis: '',
          leftSuperiorLimbicKeratoconjunctivitis: '',
          rightSuperiorLimbicKeratoconjunctivitis: '',
          binocularVisualAcuity: '',
        },
      });
    } else {
      reset({
        keratometry: {
          rightHoz1: anteriorEye?.keratometry?.rightHoz1,
          rightHoz2: anteriorEye?.keratometry?.rightHoz2,
          rightHeight1: anteriorEye?.keratometry?.rightHeight1,
          rightHeight2: anteriorEye?.keratometry?.rightHeight2,
          leftHoz1: anteriorEye?.keratometry?.leftHoz1,
          leftHoz2: anteriorEye?.keratometry?.leftHoz2,
          leftHeight1: anteriorEye?.keratometry?.leftHeight1,
          leftHeight2: anteriorEye?.keratometry?.leftHeight2,
        },
        leftAndRightAnteriorEye: {
          leftPupilSize: anteriorEye?.leftAndRightAnteriorEye?.leftPupilSize,
          rightPupilSize: anteriorEye?.leftAndRightAnteriorEye?.rightPupilSize,
          leftHVID: anteriorEye?.leftAndRightAnteriorEye?.leftHVID,
          rightHVID: anteriorEye?.leftAndRightAnteriorEye?.rightHVID,
          leftConjunctivalRedness:
            anteriorEye?.leftAndRightAnteriorEye?.leftConjunctivalRedness,
          rightConjunctivalRedness:
            anteriorEye?.leftAndRightAnteriorEye?.rightConjunctivalRedness,
          leftCornealInfiltrates:
            anteriorEye?.leftAndRightAnteriorEye?.leftCornealInfiltrates,
          rightCornealInfiltrates:
            anteriorEye?.leftAndRightAnteriorEye?.rightCornealInfiltrates,
          leftCornealNeovascularisation:
            anteriorEye?.leftAndRightAnteriorEye?.leftCornealNeovascularisation,
          rightCornealNeovascularisation:
            anteriorEye?.leftAndRightAnteriorEye
              ?.rightCornealNeovascularisation,
          leftCornealOedema:
            anteriorEye?.leftAndRightAnteriorEye?.leftCornealOedema,
          rightCornealOedema:
            anteriorEye?.leftAndRightAnteriorEye?.rightCornealOedema,
          leftCornealStaining:
            anteriorEye?.leftAndRightAnteriorEye?.leftCornealStaining,
          rightCornealStaining:
            anteriorEye?.leftAndRightAnteriorEye?.rightCornealStaining,
          leftEndothelialBlebs:
            anteriorEye?.leftAndRightAnteriorEye?.leftEndothelialBlebs,
          rightEndothelialBlebs:
            anteriorEye?.leftAndRightAnteriorEye?.rightEndothelialBlebs,
          leftEndothelialPolymegethism:
            anteriorEye?.leftAndRightAnteriorEye?.leftEndothelialPolymegethism,
          rightEndothelialPolymegethism:
            anteriorEye?.leftAndRightAnteriorEye?.rightEndothelialPolymegethism,
          leftEpithelialMicrocysts:
            anteriorEye?.leftAndRightAnteriorEye?.leftEpithelialMicrocysts,
          rightEpithelialMicrocysts:
            anteriorEye?.leftAndRightAnteriorEye?.rightEpithelialMicrocysts,
          leftLimbalRedness:
            anteriorEye?.leftAndRightAnteriorEye?.leftLimbalRedness,
          rightLimbalRedness:
            anteriorEye?.leftAndRightAnteriorEye?.rightLimbalRedness,
          leftMeibomianDysfunction:
            anteriorEye?.leftAndRightAnteriorEye?.leftMeibomianDysfunction,
          rightMeibomianDysfunction:
            anteriorEye?.leftAndRightAnteriorEye?.rightMeibomianDysfunction,
          leftPapillaryConjunctivitis:
            anteriorEye?.leftAndRightAnteriorEye?.leftPapillaryConjunctivitis,
          rightPapillaryConjunctivitis:
            anteriorEye?.leftAndRightAnteriorEye?.rightPapillaryConjunctivitis,
          leftSuperiorLimbicKeratoconjunctivitis:
            anteriorEye?.leftAndRightAnteriorEye
              ?.leftSuperiorLimbicKeratoconjunctivitis,
          rightSuperiorLimbicKeratoconjunctivitis:
            anteriorEye?.leftAndRightAnteriorEye
              ?.rightSuperiorLimbicKeratoconjunctivitis,
          binocularVisualAcuity:
            anteriorEye?.leftAndRightAnteriorEye?.binocularVisualAcuity || '',
        },
      });
    }
  }, [showCLForm, anteriorEye, reset]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {(showCLForm ||
        !anteriorEye ||
        Object.keys(anteriorEye).length === 0) && (
        <Typography sx={{ ml: 1, mt: 2, mb: 2 }} color={'red'}>
          * There's no Anterior Eye added for this clinical record, please add
          and save!
        </Typography>
      )}
      <Keratometry keratometry={anteriorEye?.keratometry} />
      <LeftAndRightAnteriorEye
        leftAndRightAnteriorEye={anteriorEye?.leftAndRightAnteriorEye}
        leftHVID={leftHVID}
        setLeftHVID={(leftHVID: string) => setLeftHVID(leftHVID)}
        rightHVID={rightHVID}
        setRightHVID={(rightHVID: string) => setRightHVID(rightHVID)}
        selectedRow={selectedRow}
        clinicalRecords={clinicalRecords}
        getAnteriorEyeRecords={getAnteriorEyeRecords}
      />
      <Box sx={{ display: 'flex', flexDirection: 'row-reverse', mr: 2, mt: 2 }}>
        <LoadingButton
          type="submit"
          variant="contained"
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          Save and Next
        </LoadingButton>
      </Box>
    </FormProvider>
  );
};
export default AnteriorEye;
